import { Button, Modal, Typography } from 'components';
import { CloseIcon } from 'lib/svgs/Icons';
import { useLocale, useTranslations } from 'next-intl';
import prettyBytes from 'pretty-bytes';
import { useTranslationMemoryUpload } from 'providers/TranslationMemoryProviders/useTranslationMemoryUpload';
import {
  ChangeEvent,
  FC,
  Reducer,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';
import { cn } from 'utils/cn';
import { v4 as uuidv4 } from 'uuid';
import CardFile, { IFileTM } from './CardFile';

import { toaster } from 'helpers/toaster';
import { getExtension } from 'utils/getExtension';
import FileAdd from './FileAdd';
interface IModalUploadTM {
  open: boolean;
  onCancel: () => void;
  searchRefetch: () => void;
}

export const ALLOWED_TYPE_TM = 'tmx';

const ModalUploadTM: FC<IModalUploadTM> = ({ open, onCancel, searchRefetch }) => {
  const t = useTranslations();
  const locale = useLocale();
  const [uploading, setUploading] = useState(false);

  const refModal = useRef<HTMLDivElement>(null);

  const { mutateAsync: translationMemoryUpload } = useTranslationMemoryUpload();

  const [cardsFile, setCardsFiles] = useReducer<
    Reducer<IFileTM[], { state?: IFileTM; type: 'update' | 'add' | 'remove' | 'reset' }>
  >((oldState, action) => {
    if (action.type === 'update')
      return oldState.map((state) =>
        state.id === action?.state?.id ? { ...state, ...action.state } : state
      );
    if (action.type === 'add' && action?.state) return [...oldState, action.state];
    if (action.type === 'remove' && action.state)
      return oldState.filter((state) => state.id !== action?.state?.id);
    if (action.type === 'reset') return [];
    return oldState;
  }, []);

  const isUploaded =
    uploading &&
    cardsFile.length > 0 &&
    cardsFile.every((cardFile) => cardFile.status !== 'processing') &&
    cardsFile.some((cardFile) => cardFile.status === 'processed');

  const isDisabledUpload =
    uploading ||
    !cardsFile.length ||
    cardsFile.every((cardFile) => cardFile.status === 'processed');

  const initFile = useCallback(
    async (file: File) => {
      const extension = getExtension(file);

      const cardFile = {
        id: uuidv4(),
        file: file,
        filename: file?.name,
        filesize: prettyBytes(file.size, { locale }),
        percentage: 0,
        status: 'processing'
      } as IFileTM;

      setCardsFiles({
        state: cardFile,
        type: 'add'
      });

      if (extension && ALLOWED_TYPE_TM !== extension) {
        return setCardsFiles({
          state: {
            ...cardFile,
            status: 'error-in-type'
          },
          type: 'update'
        });
      }
    },
    [locale, setCardsFiles]
  );

  const loadFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e?.target?.files;

      if (!!selectedFiles?.length) [...selectedFiles]?.forEach(initFile);
    },
    [initFile]
  );

  const uploadFile = useCallback(
    async (cardFile: IFileTM) => {
      try {
        const response = await translationMemoryUpload({ file: cardFile.file });
        if (response.errors?.length) {
          return setCardsFiles({
            state: {
              ...cardFile,
              uid: undefined,
              status: 'error-in-upload'
            },
            type: 'update'
          });
        }

        return setCardsFiles({
          state: {
            ...cardFile,
            uid: response?.uid as string,
            percentage: 0,
            status: 'processing'
          },
          type: 'update'
        });
      } catch (error) {
        setCardsFiles({
          state: {
            ...cardFile,
            status: 'error-in-upload'
          },
          type: 'update'
        });
        setUploading(false);
      }
    },
    [translationMemoryUpload]
  );

  const onRemove = useCallback(
    (id: string) => setCardsFiles({ state: { id }, type: 'remove' }),
    [setCardsFiles]
  );

  const onUpdate = useCallback(
    (cardFile: IFileTM) => setCardsFiles({ state: cardFile, type: 'update' }),
    [setCardsFiles]
  );

  const onRetry = useCallback((cardFile: IFileTM) => uploadFile(cardFile), [uploadFile]);

  const onSuccess = useCallback(() => {
    if (isUploaded) {
      toaster.success(
        t('messages.success.translation_search_upload.paragraph'),
        t('messages.success.translation_search_upload.title')
      );
      searchRefetch();
      setUploading(false);
    }
  }, [isUploaded, searchRefetch, t]);

  const handleUpload = useCallback(() => {
    if (!uploading) {
      setUploading(true);
      cardsFile
        .filter((cardFile) => cardFile.status !== 'processed')
        .forEach((cardFile) => uploadFile(cardFile));
    }
  }, [cardsFile, uploadFile, uploading]);

  const handleCancel = useCallback(() => {
    onCancel();
    setCardsFiles({ type: 'reset' });
    setUploading(false);
  }, [onCancel]);

  useEffect(() => {
    onSuccess();

    return () => setUploading(false);
  }, [onSuccess]);

  return (
    <Modal
      zDialog={cn('!z-[1999]')}
      zDialogBackdrop="z-[1000]"
      onClose={handleCancel}
      open={open}
      className={'w-[572px]'}
    >
      <div
        className={cn(
          'overflow-y-auto p-6',
          'relative flex w-full flex-col gap-4 rounded bg-white shadow-custom'
        )}
        ref={refModal}
      >
        <div className="absolute right-2 top-2 z-[1000]">
          <Button
            size="small"
            variant="icon"
            color="neutral"
            className="!w-8 rounded-full"
            onClick={handleCancel}
            startAdornment={<CloseIcon />}
          />
        </div>
        <div className="mb-2 flex flex-col gap-1">
          <Typography variant="medium4xl" className="text-tertiary-100 ">
            {t('translation_memory.modal_upload.title')}
          </Typography>
          <Typography variant="light3">{t('translation_memory.modal_upload.paragraph')}</Typography>
        </div>
        <FileAdd onChange={loadFile} />
        {cardsFile.map(({ id, ...cardFile }) => (
          <CardFile
            key={id}
            onRemove={onRemove}
            onUpdate={onUpdate}
            onRetry={onRetry}
            {...{ id, ...cardFile }}
          />
        ))}
        <div className="mt-6 flex justify-end">
          <Button
            size="large"
            variant="text"
            className="!w-8 rounded-full"
            onClick={handleCancel}
            label={t('translation_memory.modal_upload.cancel')}
          />
          <Button
            disabled={isDisabledUpload}
            size="large"
            variant="contained"
            className="!w-8 rounded-full"
            onClick={handleUpload}
            label={t('translation_memory.modal_upload.upload')}
            loading={uploading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ModalUploadTM;
